import { useEffect, useState } from "react";
import { Upload, Trash2, Save, Plus, X, UtensilsCrossed, CalendarCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fecha } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];
const semanaVacia = (n) => ({ nombre: `Semana ${n}`, dias: DIAS.map((d) => ({ dia: d, platos: "" })) });

export default function AdminMenu() {
  const [menu, setMenu] = useState(null);
  const [paginas, setPaginas] = useState([]);
  const [semanas, setSemanas] = useState([]);
  const [subiendo, setSubiendo] = useState(false);

  const cargar = () => {
    api.get("/menu-escolar").then(async ({ data }) => {
      setMenu(data);
      setSemanas(data.semanas?.length ? data.semanas : [semanaVacia(1)]);
      const urls = [];
      for (let i = 0; i < data.paginas; i += 1) {
        const r = await api.get(`/menu-escolar/pagina/${i}`, { responseType: "blob" });
        urls.push(URL.createObjectURL(r.data));
      }
      setPaginas(urls);
    }).catch(() => setMenu({ semanas: [], paginas: 0 }));
  };

  useEffect(cargar, []);

  const subir = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("El archivo no debe superar los 10 MB");
    setSubiendo(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.put("/menu-escolar", { base64: reader.result, nombre: file.name });
        toast.success(`Menú publicado (${data.paginas} página(s)). Ya lo ven los padres.`);
        setPaginas([]);
        cargar();
      } catch (err) {
        toast.error(apiError(err));
      }
      setSubiendo(false);
    };
    reader.readAsDataURL(file);
  };

  const borrarArchivo = async () => {
    try {
      await api.delete("/menu-escolar");
      toast.success("Archivo del menú eliminado");
      setPaginas([]);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const guardarSemanas = async () => {
    try {
      await api.put("/menu-escolar/semanas", { semanas });
      toast.success("Menú por semanas guardado");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const marcarActiva = async (indice) => {
    try {
      const { data } = await api.put("/menu-escolar/semana-activa", { indice });
      setMenu({ ...menu, semana_activa: data.semana_activa });
      toast.success(data.semana_activa === null
        ? "Ya no hay semana marcada como actual"
        : `${semanas[indice]?.nombre} es ahora la semana actual para los padres`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cambiar = (si, di, valor) => setSemanas(semanas.map((s, i) => i !== si ? s : {
    ...s, dias: s.dias.map((d, j) => (j === di ? { ...d, platos: valor } : d)),
  }));

  if (!menu) return <Loading />;

  return (
    <div data-testid="admin-menu">
      <PageTitle title="Menú escolar"
                 subtitle="Suba el menú del comedor y escríbalo por semanas. Los padres lo ven en su portal.">
        <Button className="rounded-full" onClick={guardarSemanas} data-testid="guardar-semanas-menu">
          <Save className="mr-2 h-4 w-4" /> Guardar menú por semanas
        </Button>
      </PageTitle>

      <section className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
        <div className="flex items-center gap-2 text-primary">
          <UtensilsCrossed className="h-4 w-4" />
          <h2 className="font-display text-lg font-bold">Archivo del menú (PDF o imagen)</h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Subir o reemplazar archivo</Label>
            <Input type="file" accept=".pdf,image/*" onChange={subir} disabled={subiendo}
                   data-testid="archivo-menu" />
            <p className="text-xs text-muted-foreground">
              {subiendo ? "Procesando el archivo…" : "Hasta 10 MB. Del PDF generamos las páginas como imágenes."}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Archivo publicado</Label>
            {menu.tiene_archivo ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm" data-testid="menu-archivo-actual">
                  {menu.nombre_archivo} · {menu.paginas} página(s)
                </p>
                <Button size="sm" variant="outline" className="rounded-full" onClick={borrarArchivo}
                        data-testid="borrar-menu">
                  <Trash2 className="mr-1 h-3 w-3 text-destructive" /> Quitar
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Todavía no hay archivo publicado.</p>
            )}
            {menu.actualizado_en && (
              <p className="text-xs text-muted-foreground">Actualizado el {fecha(menu.actualizado_en)}</p>
            )}
          </div>
        </div>
        {paginas.length > 0 && (
          <div className="mt-6 flex gap-4 overflow-x-auto pb-2" data-testid="menu-vista-previa">
            {paginas.map((src, i) => (
              <img key={src} src={src} alt={`Página ${i + 1}`}
                   className="h-52 rounded-md border border-border/60 bg-white" />
            ))}
          </div>
        )}
      </section>

      <section className="mt-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">Menú escrito por semana</h2>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="agregar-semana"
                  onClick={() => setSemanas([...semanas, semanaVacia(semanas.length + 1)])}>
            <Plus className="mr-1 h-3 w-3" /> Agregar semana
          </Button>
        </div>

        <div className="mt-5 grid gap-6 lg:grid-cols-2" data-testid="editor-semanas">
          {semanas.map((s, si) => (
            <article key={si} className={`rounded-lg border bg-card p-6 ${
              menu.semana_activa === si ? "border-primary ring-1 ring-primary/40" : "border-border/60"}`}>
              <div className="flex items-center gap-3">
                <Input value={s.nombre} data-testid={`semana-nombre-${si}`}
                       onChange={(e) => setSemanas(semanas.map((x, i) => i === si ? { ...x, nombre: e.target.value } : x))}
                       className="font-display text-base font-bold" />
                <Button size="icon" variant="ghost" data-testid={`quitar-semana-${si}`}
                        onClick={() => setSemanas(semanas.filter((_, i) => i !== si))}>
                  <X className="h-4 w-4 text-destructive" />
                </Button>
              </div>
              {menu.semana_activa === si ? (
                <Button size="sm" variant="secondary" className="mt-3 rounded-full"
                        onClick={() => marcarActiva(null)} data-testid={`quitar-activa-${si}`}>
                  <CalendarCheck className="mr-1 h-3 w-3" /> Semana actual · quitar marca
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="mt-3 rounded-full"
                        onClick={() => marcarActiva(si)} data-testid={`marcar-activa-${si}`}>
                  <CalendarCheck className="mr-1 h-3 w-3" /> Marcar como semana actual
                </Button>
              )}
              <div className="mt-4 space-y-3">
                {s.dias.map((d, di) => (
                  <div key={d.dia} className="space-y-1">
                    <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{d.dia}</Label>
                    <Textarea rows={2} value={d.platos} data-testid={`semana-${si}-dia-${di}`}
                              placeholder="Ej.: Arroz blanco, habichuela y pollo al caldero"
                              onChange={(e) => cambiar(si, di, e.target.value)} />
                  </div>
                ))}
              </div>
            </article>
          ))}
          {semanas.length === 0 && (
            <Empty testId="semanas-vacias">Agregue la primera semana del menú.</Empty>
          )}
        </div>

        <Button className="mt-8 rounded-full" onClick={guardarSemanas} data-testid="guardar-semanas-menu-abajo">
          <Upload className="mr-2 h-4 w-4" /> Publicar menú por semanas
        </Button>
      </section>
    </div>
  );
}
